// TicketDetailView.js — plain HTML + CSS version
import { useState, useEffect, useRef } from "react";
import swal from "sweetalert2";
import PropTypes from "prop-types";

import { api } from "utils/Api";
import "./TicketDetailView.css";
import { getSecureItem } from "utils/SecureStorage";
import { useHierarchy } from "context/HierarchyContext";

/* ── Status config ── */
const STATUS_CONFIG = {
  open: {
    color: "#e67e22",
    bg: "#fef5e7",
    border: "#fdebd0",
    icon: "radio_button_checked",
  },
  in_process: {
    color: "#3498db",
    bg: "#eaf2f8",
    border: "#d4e6f1",
    icon: "autorenew",
  },
  closed: {
    color: "#95a5a6",
    bg: "#f4f6f6",
    border: "#e5e8e8",
    icon: "cancel",
  },
};

/* ── Formatters ── */
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

function TicketDetailView({ ticket, onBack, currentUserId, ticketStatuses }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [usersMap, setUsersMap] = useState({});
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const userRole = getSecureItem("role_id")
  const textareaRef = useRef(null);
  const { orgId } = useHierarchy();
  const organization_id = JSON.parse(getSecureItem("currentUser"))?.organization_id;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setStatusDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ── Fetch ── */
  useEffect(() => {
    if (!ticket) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await api.ticket.getTicketDetails(ticket.id, orgId);
        setDetails(res);
      } catch {
        
        onBack();
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [ticket?.id]);

  const handleStatusChange = async (statusId) => {
    try {
      await api.ticket.updateTicketDetails(details.id, {
        ticket_status_id: statusId,
        organization_id: orgId || organization_id
      });

      const selectedStatus = ticketStatuses.find((s) => s.id === statusId);

      // Update UI instantly
      setDetails((prev) => ({
        ...prev,
        ticket_status: selectedStatus,
        status: selectedStatus?.name,
      }));

      setStatusDropdownOpen(false);
    } catch (err) {
    }
  };

  useEffect(() => {
    if (!details?.messages?.length) return;

    const fetchUsers = async () => {
      try {
        // Get unique sender IDs
        const senderIds = [...new Set(details.messages.map((msg) => msg.sender_id))];

        const users = {};

        await Promise.all(
          senderIds.map(async (id) => {
            const res = await api.user.getUserDetails(id);
            users[id] = res.name;
          })
        );

        setUsersMap(users);
      } catch (err) {
      }
    };

    fetchUsers();
  }, [details?.messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      setSending(true);

      const payload = {
        ticket_id: ticket.id,
        message: newMessage.trim(),
      };

      const res = await api.messages.createMessage(payload);

      // Optimistic update (append message instantly)
      setDetails((prev) => ({
        ...prev,
        messages: [...prev.messages, res],
      }));

      setNewMessage("");
      textareaRef.current?.focus();

    } catch (err) {
    } finally {
      setSending(false);
      // 👇 Focus AFTER re-enabling textarea
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    }
  };

  /* ── Auto-scroll ── */
  useEffect(() => {
    if (details?.messages?.length) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [details]);

  const statusKey = details?.ticket_status.name?.toLowerCase()?.replace(" ", "_") || "open";
  const status = STATUS_CONFIG[statusKey] || STATUS_CONFIG.open;

  /* LOADING */
  if (loading) {
    return (
      <div className="tdv-loading">
        <div className="tdv-spinner" />
        <p className="tdv-loading-text">Loading ticket…</p>
      </div>
    );
  }

  if (!details) return null;

  /* RENDER */
  return (
    <>
      {/* ── HEADER ── */}
      <div className="tdv-header">
        <button type="button" className="tdv-back-btn" onClick={onBack} aria-label="Back to list">
          <span className="material-icons">arrow_back</span>
        </button>

        <div className="tdv-header-info">
          <p className="tdv-subject">{details.subject}</p>
          <p className="tdv-ticket-id">TKT-{String(details.id).padStart(4, "0")}</p>
        </div>

        {/* <div
          className="tdv-status-badge"
          style={{
            backgroundColor: status.bg,
            borderColor: status.border,
          }}
        >
          <span className="material-icons" style={{ color: status.color }}>
            {status.icon}
          </span>
          <p className="tdv-status-text" style={{ color: status.color }}>
            {details.ticket_status.name?.replace("_", " ")}
          </p>
        </div> */}
        <div ref={dropdownRef} style={{ position: "relative" }}>
          {/* STATUS BUTTON */}
          <div
            className="tdv-status-badge"
            onClick={() => {
              if (Number(userRole) !== 5) {
                setStatusDropdownOpen((prev) => !prev);
              }
            }}
            style={{
              backgroundColor: status.bg,
              borderColor: status.border,
              cursor: Number(userRole) === 5 ? "default" : "pointer",
            }}
          >
            <span className="material-icons" style={{ color: status.color }}>
              {status.icon}
            </span>
            

            <p className="tdv-status-text" style={{ color: status.color }}>
              {details.ticket_status?.name}
            </p>

            {Number(userRole) !== 5 && (
              <span className="material-icons" style={{ fontSize: 16 }}>
                keyboard_arrow_down
              </span>
            )}
          </div>

          {/* DROPDOWN */}
          {statusDropdownOpen && (
            <div
              style={{
                position: "absolute",
                top: "110%",
                right: 0,
                background: "#fff",
                border: "1px solid #e9ecef",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                minWidth: "160px",
                zIndex: 999,
              }}
            >
              {ticketStatuses.map((statusOption) => {
                const isActive = statusOption.id === details.ticket_status?.id;

                return (
                  <div
                    key={statusOption.id}
                    onClick={() => handleStatusChange(statusOption.id)}
                    style={{
                      padding: "10px 14px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      backgroundColor: isActive ? "#e8f7fc" : "transparent",
                    }}
                    onMouseEnter={(e) =>
                      !isActive && (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                    onMouseLeave={(e) =>
                      !isActive && (e.currentTarget.style.backgroundColor = "transparent")
                    }
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? "#17c1e8" : "#344767",
                      }}
                    >
                      {statusOption.name}
                    </span>

                    {isActive && (
                      <span className="material-icons" style={{ fontSize: 16, color: "#17c1e8" }}>
                        check
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <hr className="tdv-divider" />

      {/* SCROLLABLE BODY */}
      <div className="tdv-body">
        {/* Description */}
        <div className="tdv-desc-card">
          <div className="tdv-desc-label">
            <span className="material-icons">description</span>
            <p className="tdv-desc-label-text">Description</p>
          </div>
          <p className="tdv-desc-text">{details.description}</p>
        </div>

        {/* Created date */}
        <div className="tdv-created">
          <span className="material-icons">schedule</span>
          <p className="tdv-created-text">
            {fmtDate(details.created_at)} · {fmtTime(details.created_at)}
          </p>
        </div>

        <hr className="tdv-divider--spaced" />

        {/* Messages heading */}
        <div className="tdv-messages-header">
          <span className="material-icons">chat_bubble_outline</span>
          <p className="tdv-messages-title">Messages</p>
          <div className="tdv-messages-count">
            <p className="tdv-messages-count-text">{details.messages?.length ?? 0}</p>
          </div>
        </div>

        {/* Empty state */}
        {(!details.messages || details.messages.length === 0) && (
          <div className="tdv-empty">
            <span className="material-icons">forum</span>
            <p className="tdv-empty-text">No messages yet.</p>
          </div>
        )}

        {/* Message bubbles */}
        {details.messages?.map((msg) => {
          const isCurrentUser = msg.sender_id === currentUserId;

          return (
            <div
              key={msg.id}
              className={`tdv-msg-wrapper ${
                isCurrentUser ? "tdv-msg-wrapper--right" : "tdv-msg-wrapper--left"
              }`}
            >
              {/* Sender */}
              <p className="tdv-msg-sender">
                {isCurrentUser ? "You" : usersMap[msg.sender_id] || "Loading..."}
              </p>

              {/* Bubble */}
              <div
                className={`tdv-bubble ${isCurrentUser ? "tdv-bubble--user" : "tdv-bubble--other"}`}
              >
                <p className="tdv-bubble-text">{msg.message}</p>
              </div>

              {/* Timestamp */}
              <p className="tdv-msg-time">
                {fmtDate(msg.created_at)} · {fmtTime(msg.created_at)}
              </p>
            </div>
          );
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Chat Input ── */}
      <div className="tdv-chat-input">
        <textarea
          ref={textareaRef}
          placeholder="Type your message..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          className="tdv-textarea"
          rows={1}
          disabled={sending}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
        />

        <button
          type="button"
          className="tdv-send-btn"
          onClick={handleSendMessage}
          disabled={sending || !newMessage.trim()}
        >
          <span className="material-icons">send</span>
        </button>
      </div>
    </>
  );
}

TicketDetailView.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
  }),
  onBack: PropTypes.func.isRequired,
  ticketStatuses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  currentUserId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
};

TicketDetailView.defaultProps = {
  ticket: null,
  currentUserId: null,
};

export default TicketDetailView;